import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface AuthState {
  token: string | null;
  userId: string | null;
  clinicId: string | null;
  set: (next: { token: string; userId: string; clinicId?: string }) => void;
  setClinic: (clinicId: string) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
}

// Bootstrap dev token from env — only active in development mode.
// Guarded by import.meta.env.DEV so a production build can never pick up a
// hardcoded token even if VITE_DEV_AUTH_TOKEN is accidentally present in the
// build environment.
const DEV_TOKEN =
  import.meta.env.DEV
    ? (import.meta.env.VITE_DEV_AUTH_TOKEN as string | undefined)
    : undefined;
const DEV_CLINIC_ID =
  import.meta.env.DEV
    ? (import.meta.env.VITE_DEV_CLINIC_ID as string | undefined)
    : undefined;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: DEV_TOKEN ?? null,
      userId: DEV_TOKEN ? "dev-user" : null,
      clinicId: DEV_CLINIC_ID ?? null,

      set: ({ token, userId, clinicId }) =>
        set({ token, userId, clinicId: clinicId ?? get().clinicId }),

      setClinic: (clinicId) => set({ clinicId }),

      clear: () => set({ token: null, userId: null, clinicId: null }),

      isAuthenticated: () => Boolean(get().token),
    }),
    {
      name: "vetaudit-auth",
      storage: createJSONStorage(() => localStorage),
      // Don't persist dev-token state — that's env-driven each boot
      partialize: (state) =>
        DEV_TOKEN ? {} : { token: state.token, userId: state.userId, clinicId: state.clinicId },
    },
  ),
);
